import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  DEFAULT_DAYS,
  DEFAULT_MAIN_WEATHERS,
  DEFAULT_SUB_WEATHERS,
  DEFAULT_TIER,
} from '../top_ranker/statMetrics';
import { findStat } from './cardStats';
import { FAILED_AREA, UNKNOWN_AREA, createRouteResolver, destinationOf, fetchWithFallbackDays } from './routeDestinations';
import { countObjects } from './routeObjects';

// 최근 기간에 루트 통계가 없으면 더 긴 기간으로 다시 조회한다
const ROUTE_STATS_DAYS = [DEFAULT_DAYS, 90];

// 화면을 다시 그려도 조회 결과를 다시 받지 않도록 모듈 단위로 하나만 둔다.
const sharedResolver = createRouteResolver({
  fetchRouteStats: (characterName, bestWeapon) => fetchWithFallbackDays(ROUTE_STATS_DAYS, (days) => axios.get('/api/external/bser/character-detail', {
    params: {
      days,
      tier: DEFAULT_TIER,
      mainWeathers: DEFAULT_MAIN_WEATHERS.join(','),
      subWeathers: DEFAULT_SUB_WEATHERS.join(','),
      characterName,
      bestWeapon,
    },
  }).then((r) => r.data.routeStats)),
  fetchRouteDetail: (routeId) => axios.get('/api/external/bser/route-detail', { params: { routeId } }).then((r) => r.data),
});

/**
 * 인식한 조합(teams)의 모든 카드가 가장 많이 쓰는 루트를 구해, 마지막 도착 지역과 후반 교체 아이템이 쓰는 오브젝트 수를 만든다.
 * stats는 Top Ranker 통계(무기를 못 읽은 카드는 그 캐릭터의 최다 사용 무기로 추정). 통계가 없으면 아직 조회하지 않는다.
 * 돌려주는 값: entries — [{ teamIndex, characterName, weaponName, area, objectCounts, unavailable }]
 *   - area: 도착 지역(null이면 조회 중)
 *   - objectCounts: countObjects 결과(null이면 조회 중이거나 알 수 없음), unavailable: 루트 통계가 없거나 조회 실패/무기를 몰라 알 수 없음
 */
export default function useRouteDestinations({ teams, stats }) {
  // `${캐릭터}|${무기}` → { detail } (루트 상세, 루트 통계가 없으면 null) 또는 { failed: true }
  const [results, setResults] = useState({});

  const cards = useMemo(() => {
    if (!teams || !stats.length) {
      return [];
    }
    return teams.flatMap((team, teamIndex) => team.map((card) => {
      const characterName = card.character.name;
      const { stat } = findStat(stats, characterName, card.weapon && card.weapon.name);
      return { teamIndex, characterName, weaponName: stat ? stat.bestWeapon : null };
    }));
  }, [teams, stats]);

  useEffect(() => {
    let cancelled = false;
    const keys = new Map();
    cards.forEach((c) => {
      if (c.weaponName) {
        keys.set(`${c.characterName}|${c.weaponName}`, c);
      }
    });
    keys.forEach((c, key) => {
      sharedResolver.resolve(c.characterName, c.weaponName)
        .then((detail) => { if (!cancelled) setResults((prev) => (prev[key] ? prev : { ...prev, [key]: { detail } })); })
        .catch(() => { if (!cancelled) setResults((prev) => ({ ...prev, [key]: { failed: true } })); });
    });
    return () => { cancelled = true; };
  }, [cards]);

  const entries = useMemo(() => cards.map((c) => {
    // 통계에 없는 캐릭터(무기를 알 수 없음)는 조회할 수 없으니 알 수 없음으로 둔다
    if (!c.weaponName) {
      return { ...c, area: UNKNOWN_AREA, objectCounts: null, unavailable: true };
    }
    const result = results[`${c.characterName}|${c.weaponName}`];
    if (!result) {
      return { ...c, area: null, objectCounts: null, unavailable: false };
    }
    if (result.failed) {
      return { ...c, area: FAILED_AREA, objectCounts: null, unavailable: true };
    }
    return {
      ...c,
      area: destinationOf(result.detail),
      // 루트 통계가 없으면(detail이 null) 오브젝트 수도 알 수 없다
      objectCounts: result.detail ? countObjects(result.detail.lateGameItems) : null,
      unavailable: !result.detail,
    };
  }), [cards, results]);

  return { entries };
}
